/**
 * Development Configuration for Testing
 *
 * This file allows developers to override UV index values for testing purposes.
 *
 * TWO TESTING MODES:
 *
 * 1. MANUAL UV OVERRIDE (highest priority):
 *    - Set ENABLE_DEV_OVERRIDE to true
 *    - Set MANUAL_UV_INDEX to your desired value (0-15)
 *    - Completely bypasses all other logic
 *
 * 2. TIME SIMULATION (works independently):
 *    - Set SIMULATE_TIME_OF_DAY to true
 *    - Set SIMULATED_HOUR to test different times (0-23)
 *    - Uses time-based UV calculation with your chosen hour
 *    - Works even when ENABLE_DEV_OVERRIDE is false
 *
 * TESTING SCENARIOS:
 * - UV 0: Night sky with stars and moon
 * - UV 1-2: Low UV, light blue background, soft yellow sun
 * - UV 3-5: Moderate UV, yellow/gold background
 * - UV 6-7: High UV, orange background, brighter sun
 * - UV 8-10: Very high UV, red background, intense white sun
 * - UV 11+: Extreme UV, dark red background, pure white sun
 */

// DEV OVERRIDE SETTINGS
export const DEV_CONFIG = {
  // Set to true to enable manual UV override
  ENABLE_DEV_OVERRIDE: false,

  // Manual UV index value (0-15)
  // 0 = Night (shows stars), 1-2 = Low, 3-5 = Moderate, 6-7 = High, 8-10 = Very High, 11+ = Extreme
  MANUAL_UV_INDEX: 0,

  // Force darkness state (overrides solar calculations)
  // Set to true to force night mode, false to force day mode, null to use calculations
  FORCE_DARKNESS: false,

  // Simulate different times of day for testing
  SIMULATE_TIME_OF_DAY: false,
  SIMULATED_HOUR: 12, // 0-23 (24-hour format)

  // Premium features toggle
  // Set to false to hide premium upselling and make all premium features available to all users
  // Set to true to show normal premium/free user experience
  ENABLE_PREMIUM_VERSION: false,

  // Breakdown button toggle
  // Set to true to show the "breakdown" link below the time info
  ENABLE_BREAKDOWN: false,
};

// Quick test presets for common scenarios
export const TEST_PRESETS = {
  NIGHT: { uvIndex: 0, description: "Night time - stars and moon" },
  DAWN: { uvIndex: 1, description: "Early morning - very low UV" },
  MORNING: { uvIndex: 3, description: "Morning - moderate UV" },
  MIDDAY: { uvIndex: 8, description: "Midday - very high UV" },
  EXTREME: { uvIndex: 12, description: "Extreme UV conditions" },
};

/**
 * Get the manual UV index if dev override is enabled
 * @returns {number|null} Manual UV index or null if disabled
 */
export const getManualUVIndex = () => {
  if (!DEV_CONFIG.ENABLE_DEV_OVERRIDE) {
    return null;
  }

  // Clamp UV index between 0 and 15
  return Math.max(0, Math.min(15, DEV_CONFIG.MANUAL_UV_INDEX));
};

/**
 * Get simulated hour if time simulation is enabled
 * @returns {number|null} Simulated hour or null if disabled
 */
export const getSimulatedHour = () => {
  if (!DEV_CONFIG.SIMULATE_TIME_OF_DAY) {
    return null;
  }

  // Clamp hour between 0 and 23
  return Math.max(0, Math.min(23, DEV_CONFIG.SIMULATED_HOUR));
};

/**
 * Get forced darkness state if enabled
 * @returns {boolean|null} Forced darkness state or null if not forced
 */
export const getForcedDarkness = () => {
  return DEV_CONFIG.FORCE_DARKNESS;
};

/**
 * Check if premium version should be enabled
 * @returns {boolean} True if premium version is enabled, false to show all features for free
 */
export const isPremiumVersionEnabled = () => {
  return DEV_CONFIG.ENABLE_PREMIUM_VERSION;
};

/**
 * Check if the breakdown button should be shown
 * @returns {boolean} True if breakdown feature is enabled
 */
export const isBreakdownEnabled = () => {
  return DEV_CONFIG.ENABLE_BREAKDOWN;
};

/**
 * Log current dev configuration to console
 */
export const logDevConfig = () => {
  if (process.env.NODE_ENV === "development") {
    console.group("🔧 Dev Configuration");
    console.log("Manual UV override:", DEV_CONFIG.ENABLE_DEV_OVERRIDE);
    if (DEV_CONFIG.ENABLE_DEV_OVERRIDE) {
      console.log("Manual UV Index:", DEV_CONFIG.MANUAL_UV_INDEX);
    }
    console.log(
      "Force darkness:",
      DEV_CONFIG.FORCE_DARKNESS !== null
        ? DEV_CONFIG.FORCE_DARKNESS
        : "disabled"
    );
    console.log("Time simulation:", DEV_CONFIG.SIMULATE_TIME_OF_DAY);
    if (DEV_CONFIG.SIMULATE_TIME_OF_DAY) {
      console.log("Simulated hour:", DEV_CONFIG.SIMULATED_HOUR);
    }
    console.log("Premium version enabled:", DEV_CONFIG.ENABLE_PREMIUM_VERSION);
    if (!DEV_CONFIG.ENABLE_PREMIUM_VERSION) {
      console.log("⭐ All premium features are available for free!");
    }
    console.groupEnd();
  }
};
